import { useState } from 'react'
import { Checkbox } from 'semantic-ui-react'
import { useRouter } from 'next/router'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_FOLDER_STATE } from 'redux/folders/actions/types'

import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'

import Modal from 'components/elements/Modal'
import PhatButton from 'components/formElements/PhatButton'
import ProgressLoadingBar from 'components/elements/ProgressLoadingBar'

import styles from './editMultipleDecksSettings.module.scss'
import CheckboxFancybox from 'components/formElements/CheckboxFancybox'

const EditMultipleDecksSettings = () => {
  const selectedDecks = useAppSelector(state => state.folders.selectedDecks)
  const editMultipleDecksSettingsOpen = useAppSelector(state => state.folders.editMultipleDecksSettingsOpen)

  const [setFolderState] = useActionless(SET_FOLDER_STATE)

  const router = useRouter()

  const [privateDeck, setPrivate] = useState(false)
  const [unlisted, setUnlisted] = useState(false)
  const [theorycrafted, setTheorycrafted] = useState(false)

  const [loading, setLoading] = useState(false)
  const [updatedCount, setUpdatedCount] = useState<null | number>(null)

  const deckIds = Object.keys(selectedDecks).filter(id => selectedDecks[Number(id)])

  const handleUpdateDecks = async () => {
    const args = {
      private: privateDeck,
      unlisted: unlisted,
      theorycrafted: theorycrafted,
    }

    let localUpdatedCount = 0

    setLoading(true)

    try {
      for (const deckId of deckIds) {
        await DeckService.update(deckId, args)

        localUpdatedCount += 1
        setUpdatedCount(localUpdatedCount)

        // Artificial delay to make it feel like it's doing something (and not get throttled)
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      ToastService.create(`${deckIds.length} deck(s) updated`, 'Deck service', 'success')

      router.reload()
    } catch (err) {
      ToastService.create(err, 'Deck service')

      setLoading(false) // only set loading to false if there was an error since we're reloading the page
    }
  }

  return (
    <Modal
      closeButton
      open={editMultipleDecksSettingsOpen}
      onClose={() => setFolderState({ editMultipleDecksSettingsOpen: false })}>
      <h2>Update {deckIds.length} deck(s)</h2>

      <p className={styles.warning}>
        Warning: Updating all ({deckIds.length}) decks at once will override whatever values for private, unlisted, etc
        they currently have set. This action cannot be undone in bulk after saving.
      </p>

      <div className={styles.checkboxes}>
        <CheckboxFancybox
          checked={privateDeck}
          header="Private"
          onClick={() => setPrivate(!privateDeck)}
          description="Private decks cannot be seen by anyone but you (and users you share the deck with via collaborators)"
        />

        <CheckboxFancybox
          checked={unlisted}
          header="Unlisted"
          onClick={() => setUnlisted(!unlisted)}
          description="Unlisted decks do not appear in search results or on user pages for anyone but their owner"
        />

        <CheckboxFancybox
          checked={theorycrafted}
          header="Theorycrafted"
          onClick={() => setTheorycrafted(!theorycrafted)}
          description='Theorycrafted decks are not included by default in "In decks" view'
        />
      </div>

      <div className={styles.controls} onClick={handleUpdateDecks}>
        <PhatButton disabled={loading} color="green">
          Update all ({deckIds.length}) deck(s)
        </PhatButton>
      </div>

      {updatedCount !== null && (
        <ProgressLoadingBar
          completionPercentage={updatedCount / deckIds.length}
          label={`Updating Decks ${updatedCount} of ${deckIds.length}`}
        />
      )}
    </Modal>
  )
}

export default EditMultipleDecksSettings
